import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import UserContext from "../../../../hooks/useUserContext";

const useManageModalUrl = (
	pageTitle: string, // e.g. "Orders"
	activeUuid: string | null,
	setActiveUuid: (uuid: string) => void,
	isModalOpen: boolean,
	setIsModalOpen: (isOpen: boolean) => void,
	setCallFromFilter?: (value: boolean) => void,
	callFromFilter?: boolean
) => {
	const location = useLocation();
	const { user } = useContext(UserContext);
	// ── Rewrite exactly one `pageTitle=...` and one `uuid=...`, then replace URL
	const rewriteModalParams = (
		searchParams: URLSearchParams,
		modalValue: string,
		uuid: string
	) => {
		// Set two separate keys — not one combined value
		searchParams.set(pageTitle, modalValue);
		searchParams.set("uuid", uuid);

		const newUrl = `${location.pathname}?${searchParams.toString()}`;
		window.history.replaceState(null, "", newUrl);
	};

	// ── Remove both keys at once (when closing)
	const removeModalParams = (searchParams: URLSearchParams) => {
		searchParams.delete(pageTitle);
		searchParams.delete("uuid");

		const newUrl = `${location.pathname}?${searchParams.toString()}`;
		window.history.replaceState(null, "", newUrl);
	};

	// ── Called whenever we want to open the modal (row click or initial load)
	const handleModalOpen = (searchParams: URLSearchParams, uuid: string, skipStateUpdate = false) => {
		if (!skipStateUpdate) {
			setActiveUuid(uuid);
			setIsModalOpen(true);
		}

		// If URL already said "approvals-view", reflect that in context
		if (
			searchParams.get(pageTitle) === "approvals-view" &&
			setCallFromFilter
		) {
			setCallFromFilter(true);
		}

		// Decide whether to use "orders-view" or "approvals-view"
		let modalValue: string;
		if (pageTitle === "Orders") {
			modalValue = callFromFilter ? "approvals-view" : "orders-view";
		} else {
			modalValue = `${pageTitle.toLowerCase().replace(" ", "-")}-view`;
		}

		// Now rewrite both keys in one shot
		rewriteModalParams(searchParams, modalValue, uuid);

		// Persist to localStorage for copy/paste → reload
		localStorage.setItem("orders", modalValue);
		//UUID FOR DEV TEAM
		if (user.uuid !== "3933d52a-59a2-2110-9deb-b5772d4d263a") {
			localStorage.setItem("uuid", uuid);
		}
	};

	// ── Called whenever we want to close the modal
	const handleModalClose = (searchParams: URLSearchParams) => {
		// Remove both query params
		removeModalParams(searchParams);

		// Clear from localStorage so a reload won’t immediately open again
		localStorage.removeItem("orders");

		localStorage.removeItem("uuid");

		setIsModalOpen(false);
	};

	// ── Main effect: watches location.search, isModalOpen, activeUuid
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const storedOrders = localStorage.getItem("orders"); // e.g. "orders-view" or "approvals-view"
		const storedUuid = localStorage.getItem("uuid");

		// 1) If localStorage has both keys and modal is not open, restore from localStorage
		if (storedOrders && storedUuid && !isModalOpen) {
			setTimeout(() => {
				handleModalOpen(searchParams, storedUuid);
			}, 500);
			return;
		}

		// 2) If modal is currently closed, ensure both params are removed
		if (!isModalOpen) {
			removeModalParams(searchParams);
			return;
		}

		// 3) If modal is open (from a row-click), and activeUuid is set, write both params
		if (isModalOpen && activeUuid) {
			// Skip state update since modal is already open - just update URL and localStorage
			handleModalOpen(searchParams, activeUuid, true);
			return;
		}

		// 4) Otherwise (modal closed + no localStorage), just strip any stray params
		removeModalParams(searchParams);
	}, [location.search, isModalOpen, activeUuid, pageTitle]);
};

export default useManageModalUrl;
